/**
 * HubMissionControlPage — OBJECTIVE-0 #1224 / #1226.
 *
 * Joe's single visual pane for running the company. Five stacked panels on
 * live data, deep-linking out to GitHub for actions:
 *
 *   1. Waiting on You   — /hub/mission/waiting-on-joe (oldest first, age badge)
 *                         + interactive gate-clearing (approve / answer) via
 *                         POST /hub/mission/waiting-on-joe/{n}/clear
 *   2. Incidents        — /hub/mission/incidents (prominent only when non-empty)
 *   3. Objectives       — /hub/mission/objectives (+ queue depth, shipped 7d)
 *   4. Idea Inbox       — /hub/mission/inbox
 *   5. Fleet Health     — /hub/mission/fleet (alive/quiet/dark per agent)
 *
 * Contract: docs/features/mission-control.md. Page pattern copied from
 * HubOverviewPage: per-endpoint useState, one Promise.all with per-call
 * .catch(() => null) so any single endpoint failing renders that panel's
 * "unavailable" line without blanking the page, 30s refresh interval.
 * Panels stack single-column top→bottom (the documented order), which is
 * also the mobile behavior.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { formatTime } from '../utils/dateUtils'
import { Bars, Card, Donut, Icon, Pill, Sparkline, StatusDot } from './components'
import { PipelineBoard } from './PipelineBoard'
import { fmtAgeSecs, relTime } from './timeFormat'
import {
  hubMission,
  hubMissionPipeline, hubMissionAutonomy, hubMissionMarketing,
  type HubMissionWaitingResponse, type HubMissionWaitingItem,
  type HubMissionObjectivesResponse, type HubMissionObjective, type HubMissionInboxResponse,
  type HubMissionFleetResponse, type HubMissionFleetItem,
  type HubMissionIncidentsResponse,
  type HubMissionAgentResponse,
  type HubMissionPipelineResponse,
  type HubMissionAutonomyState,
  type HubMissionCOOOverviewResponse,
  type HubMissionCSMetricsResponse,
  type HubMissionMarketingPendingResponse, type HubMissionMarketingPRItem,
  type HubReleaseFreshnessResponse, type HubReleaseChannel, type HubReleaseStaleness,
} from './api'
import type { IconName } from './components'

const GITHUB_REPO_ISSUE_BASE = 'https://github.com/meadowlarkjoe/Tenon/issues'

/**
 * SCHEDULED_AGENTS — agent_ids that run on a fixed cadence (per the 2026-07-15
 * comment on #1226). For these, a "dark" liveness status is an alarm (red);
 * event-driven agents outside this set render "dark" as calm dim, since going
 * quiet is expected. Kept in sync with missionAgentIdentities in
 * internal/api/hub_mission_agent_handler.go.
 */
const SCHEDULED_AGENTS = new Set<string>([
  'tenon:eng:coordinator',
  'tenon:eng:ship-reviewer',
  'tenon:eng:spawn-coder',
  'tenon:product:objective-planner',
  'tenon:marketing:coordinator',
  'tenon:cs:coordinator',
])

export default function HubMissionControlPage() {
  const [waiting, setWaiting] = useState<HubMissionWaitingResponse | null>(null)
  const [incidents, setIncidents] = useState<HubMissionIncidentsResponse | null>(null)
  const [objectives, setObjectives] = useState<HubMissionObjectivesResponse | null>(null)
  const [inbox, setInbox] = useState<HubMissionInboxResponse | null>(null)
  const [fleet, setFleet] = useState<HubMissionFleetResponse | null>(null)
  const [pipeline, setPipeline] = useState<HubMissionPipelineResponse | null>(null)
  const [autonomy, setAutonomy] = useState<HubMissionAutonomyState | null>(null)
  const [coo, setCoo] = useState<HubMissionCOOOverviewResponse | null>(null)
  const [csMetrics, setCsMetrics] = useState<HubMissionCSMetricsResponse | null>(null)
  const [marketing, setMarketing] = useState<HubMissionMarketingPendingResponse | null>(null)
  const [releases, setReleases] = useState<HubReleaseFreshnessResponse | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)
  const [openAgentId, setOpenAgentId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [w, inc, obj, ib, fl, pl, au, co, cs, mk, rel] = await Promise.all([
      hubMission.waitingOnJoe().catch(() => null),
      hubMission.incidents().catch(() => null),
      hubMission.objectives().catch(() => null),
      hubMission.inbox().catch(() => null),
      hubMission.fleet().catch(() => null),
      hubMissionPipeline().catch(() => null),
      hubMissionAutonomy.get().catch(() => null),
      hubMission.cooOverview().catch(() => null),
      hubMission.csMetrics().catch(() => null),
      hubMissionMarketing.pending().catch(() => null),
      hubMission.releaseFreshness().catch(() => null),
    ])
    setWaiting(w)
    setIncidents(inc)
    setObjectives(obj)
    setInbox(ib)
    setFleet(fl)
    setPipeline(pl)
    setAutonomy(au)
    setCoo(co)
    setCsMetrics(cs)
    setMarketing(mk)
    setReleases(rel)
    setRefreshedAt(new Date())
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [load])

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 1100 }}>
      <AutonomyBanner data={autonomy} onRefresh={load} />
      <PipelineStrip data={pipeline} autonomy={autonomy} onRefresh={load} />
      <WaitingPanel data={waiting} onRefresh={load} />
      <MarketingApprovalsPanel data={marketing} onRefresh={load} />
      <IncidentsPanel data={incidents} />
      <ReleaseFreshnessPanel data={releases} />
      <ObjectivesPanel data={objectives} />
      <InboxPanel data={inbox} />
      <FleetPanel data={fleet} onOpenAgent={setOpenAgentId} />
      <COOOverviewPanel data={coo} />
      <CSMetricsPanel data={csMetrics} />

      {refreshedAt && (
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-dim)' }} className="mono">
          refreshed {formatTime(refreshedAt)}
        </div>
      )}

      {openAgentId && (
        <AgentDrawer agentId={openAgentId} onClose={() => setOpenAgentId(null)} />
      )}
    </div>
  )
}

// ── Autonomy kill switch (#1248) ────────────────────────────────────────

/** Full-width banner shown only when the fleet is paused, with a resume button.
 *  The DB flag is the source of truth the Mac-side runners poll — runner
 *  enforcement ships separately, so flipping this won't stop an in-flight tick
 *  until that lands (see tooltip + feature doc). */
function AutonomyBanner({ data, onRefresh }: {
  data: HubMissionAutonomyState | null
  onRefresh: () => void
}) {
  const [busy, setBusy] = useState(false)
  if (!data || !data.paused) return null

  const resume = async () => {
    setBusy(true)
    try {
      await hubMissionAutonomy.set(false, '')
      onRefresh()
    } catch {
      setBusy(false)
    }
  }

  return (
    <div
      className="row"
      title="The DB flag is authoritative; runner enforcement is pending — an in-flight tick may finish before pausing takes effect."
      style={{
        gap: 12, padding: '12px 16px', alignItems: 'center',
        background: 'var(--surface)', border: '1px solid var(--err)',
        borderLeft: '4px solid var(--err)', borderRadius: 'var(--radius)',
      }}
    >
      <Icon name="alert-triangle" size={16} style={{ color: 'var(--err)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--err)', letterSpacing: '0.02em' }}>
          AUTONOMY PAUSED{data.reason ? ` — ${data.reason}` : ''}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          Runner enforcement pending — the DB flag is authoritative; an in-flight tick may finish first.
        </div>
      </div>
      <button
        type="button"
        onClick={resume}
        disabled={busy}
        style={{
          padding: '6px 14px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
          borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          background: 'var(--surface-2)', color: 'var(--text)',
          fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {busy ? 'Resuming…' : 'Resume fleet'}
      </button>
    </div>
  )
}

// ── Pipeline flow board (#1248) ─────────────────────────────────────────

function PipelineStrip({ data, autonomy, onRefresh }: {
  data: HubMissionPipelineResponse | null
  autonomy: HubMissionAutonomyState | null
  onRefresh: () => void
}) {
  const [modal, setModal] = useState(false)
  const stages = data?.stages ?? []
  const stuckCount = stages.filter(s => s.stuck).length
  const paused = autonomy?.paused ?? false

  return (
    <Card
      icon="git-branch" title="Pipeline" endpoint="/hub/mission/pipeline"
      right={
        <div className="row" style={{ gap: 8 }}>
          {data && stuckCount > 0 && <Pill variant="err">{stuckCount} stuck</Pill>}
          {autonomy && !paused && (
            <button
              type="button"
              onClick={() => setModal(true)}
              title="Pause the autonomous fleet"
              style={{
                padding: '3px 10px', fontSize: 11, whiteSpace: 'nowrap',
                borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--surface-2)', color: 'var(--text)',
                fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              ⏸ Pause fleet
            </button>
          )}
        </div>
      }
      bodyPad={false}
    >
      <PipelineBoard data={data} />
      {modal && (
        <PauseConfirmModal
          onCancel={() => setModal(false)}
          onPaused={() => { setModal(false); onRefresh() }}
        />
      )}
    </Card>
  )
}

/** Confirm modal for pausing the fleet — requires a non-empty reason.
 *  Mirrors the GateClearModal pattern (overlay 0.5, centered card, token
 *  discipline). */
function PauseConfirmModal({ onCancel, onPaused }: {
  onCancel: () => void
  onPaused: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await hubMissionAutonomy.set(true, reason.trim())
      onPaused()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const confirmDisabled = busy || reason.trim() === ''

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onCancel() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', color: 'var(--text)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)', padding: 18, width: 460, maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Pause the autonomous fleet?</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Sets the fleet-pause flag. The Mac-side runners poll this flag to skip
          ticks — runner enforcement ships separately, so an in-flight tick may
          finish before the pause takes effect.
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (shown in the banner)…"
          rows={3}
          autoFocus
          disabled={busy}
          style={{
            padding: 8, fontSize: 13, resize: 'vertical',
            borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: 'var(--bg-elev)', color: 'var(--text)', fontFamily: 'inherit',
          }}
        />
        {error && <div style={{ fontSize: 12, color: 'var(--err)' }}>{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '6px 12px', fontSize: 12.5,
              borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text)',
              fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={confirmDisabled}
            style={{
              padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
              borderRadius: 'var(--radius)', border: '1px solid var(--err)',
              background: 'var(--err)', color: 'var(--accent-fg)',
              fontFamily: 'inherit',
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              opacity: confirmDisabled ? 0.6 : 1,
            }}
          >
            {busy ? 'Pausing…' : 'Pause fleet'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Panel 1: Waiting on You ─────────────────────────────────────────────

const KIND_TONE: Record<HubMissionWaitingItem['kind'], 'err' | 'warn' | 'info'> = {
  'operator': 'warn',
  'decision': 'err',
  'needs-human': 'warn',
}

const KIND_LABEL: Record<HubMissionWaitingItem['kind'], string> = {
  'operator': 'do this',
  'decision': 'your call',
  'needs-human': 'needs you',
}

function WaitingPanel({ data, onRefresh }: {
  data: HubMissionWaitingResponse | null
  onRefresh: () => void
}) {
  // Optimistically hidden issue numbers (gate cleared locally; the GitHub
  // issues cache may lag the label removal, so keep them hidden until the
  // server stops returning them — then forget, so a later re-gate reappears).
  const [cleared, setCleared] = useState<Set<number>>(new Set())
  const [modal, setModal] = useState<{ mode: 'approve' | 'answer'; item: HubMissionWaitingItem } | null>(null)

  useEffect(() => {
    if (!data) return
    setCleared(prev => {
      const present = new Set(data.items.map(it => it.issue_number))
      const next = new Set([...prev].filter(n => present.has(n)))
      return next.size === prev.size ? prev : next
    })
  }, [data])

  // Contract orders oldest-first; sort defensively so the age story holds.
  const items = useMemo(
    () => [...(data?.items ?? [])]
      .filter(it => !cleared.has(it.issue_number))
      .sort((a, b) => b.age_seconds - a.age_seconds),
    [data, cleared],
  )

  const handleCleared = (issueNumber: number) => {
    setCleared(prev => new Set(prev).add(issueNumber))
    setModal(null)
    onRefresh()
  }

  return (
    <Card
      icon="bell" title="Waiting on You" endpoint="/hub/mission/waiting-on-joe"
      right={data && items.length > 0
        ? <Pill variant="warn">{items.length}</Pill>
        : data ? <Pill variant="ok">clear</Pill> : undefined}
      bodyPad={items.length === 0}
    >
      {!data && <Unavailable />}
      {data && items.length === 0 && (
        <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Nothing needs you.
        </div>
      )}
      {items.map((it, i) => (
        // Unlike IssueRow (a single <a>), this row keeps the link and the
        // action buttons as SIBLINGS so buttons never live inside an anchor —
        // no stopPropagation games needed.
        <div
          key={it.issue_number}
          className="row"
          style={{
            padding: '8px 12px', gap: 10,
            borderBottom: i === items.length - 1 ? 0 : '1px solid var(--border)',
          }}
        >
          <a
            href={it.github_url}
            target="_blank"
            rel="noreferrer"
            className="row"
            style={{ flex: 1, minWidth: 0, gap: 10, textDecoration: 'none' }}
          >
            <StatusDot tone={KIND_TONE[it.kind] ?? 'warn'} />
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)', flex: '0 0 44px' }}>
              #{it.issue_number}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.title}
            </span>
            <Icon name="arrow-up-right" size={11} style={{ color: 'var(--text-dim)' }} />
          </a>
          <PriorityPill priority={it.priority} />
          <Pill variant="ghost">{KIND_LABEL[it.kind] ?? it.kind}</Pill>
          <AgeBadge seconds={it.age_seconds} />
          <GateActionButton label="✓ Approve" onClick={() => setModal({ mode: 'approve', item: it })} />
          <GateActionButton label="💬 Answer" onClick={() => setModal({ mode: 'answer', item: it })} />
        </div>
      ))}
      {modal && (
        <GateClearModal
          mode={modal.mode}
          item={modal.item}
          onCancel={() => setModal(null)}
          onCleared={handleCleared}
        />
      )}
    </Card>
  )
}

function GateActionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '3px 8px', fontSize: 11, whiteSpace: 'nowrap',
        borderRadius: 'var(--radius)', border: '1px solid var(--border)',
        background: 'var(--surface-2)', color: 'var(--text)',
        fontFamily: 'inherit', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}

/** Confirm-to-save modal for clearing a waiting-on-Joe gate — either a plain
 *  approval or an answer posted as a GitHub comment. */
function GateClearModal({ mode, item, onCancel, onCleared }: {
  mode: 'approve' | 'answer'
  item: HubMissionWaitingItem
  onCancel: () => void
  onCleared: (issueNumber: number) => void
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await hubMission.clearGate(item.issue_number, mode, mode === 'answer' ? text : undefined)
      onCleared(item.issue_number)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const confirmDisabled = busy || (mode === 'answer' && text.trim() === '')

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onCancel() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', color: 'var(--text)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)', padding: 18, width: 460, maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {mode === 'approve' ? 'Mark good to go?' : 'Answer and clear gate'}
        </div>
        <div className="row" style={{ gap: 8, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>#{item.issue_number}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>{item.title}</span>
        </div>
        {mode === 'approve' ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Mark good to go? This clears the gate and the pipeline proceeds.
          </div>
        ) : (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your answer…"
              rows={4}
              autoFocus
              disabled={busy}
              style={{
                padding: 8, fontSize: 13, resize: 'vertical',
                borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--bg-elev)', color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Your answer is posted as a comment and the gate clears.
            </div>
          </>
        )}
        {error && (
          <div style={{ fontSize: 12, color: 'var(--err)' }}>
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '6px 12px', fontSize: 12.5,
              borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text)',
              fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={confirmDisabled}
            style={{
              padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
              borderRadius: 'var(--radius)', border: '1px solid var(--accent-2)',
              background: 'var(--accent)', color: 'var(--accent-fg)',
              fontFamily: 'inherit',
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              opacity: confirmDisabled ? 0.6 : 1,
            }}
          >
            {busy
              ? 'Clearing…'
              : mode === 'approve' ? 'Confirm approve' : 'Confirm answer'}
          </button>
        </div>
      </div>
    </div>
  )
}

const PRIORITY_VARIANT: Record<string, 'err' | 'warn' | 'ghost'> = {
  p0: 'err',
  p1: 'warn',
}

function PriorityPill({ priority }: { priority: string }) {
  if (!priority) return null
  return (
    <Pill variant={PRIORITY_VARIANT[priority] ?? 'ghost'}>
      <span className="mono">{priority.toUpperCase()}</span>
    </Pill>
  )
}

/** Age pill — humanized (Xm/Xh/Xd) from seconds so sub-day ages don't read "0d".
 *  Warn-tone past 14 days. */
function AgeBadge({ seconds }: { seconds: number }) {
  const FOURTEEN_DAYS = 14 * 24 * 3600
  return (
    <Pill variant={seconds > FOURTEEN_DAYS ? 'warn' : 'ghost'}>
      <span className="mono">{fmtAgeSecs(seconds)}</span>
    </Pill>
  )
}

// ── Marketing Approvals (MARKETING-MC-APPROVE #1578) ─────────────────────
// Contract: docs/features/mkt-preview-approve.md §7. Open project:marketing +
// needs-human PRs awaiting Joe's visual review. Sourced live from GitHub
// Search (PRs aren't in the issues cache), so this panel renders nothing when
// GITHUB_TOKEN is unset server-side rather than showing a false "unavailable"
// — an empty/absent list here isn't itself alarming the way Incidents is.

function MarketingApprovalsPanel({ data, onRefresh }: {
  data: HubMissionMarketingPendingResponse | null
  onRefresh: () => void
}) {
  const [cleared, setCleared] = useState<Set<number>>(new Set())
  const [modal, setModal] = useState<{ mode: 'approve' | 'reject'; item: HubMissionMarketingPRItem } | null>(null)

  useEffect(() => {
    if (!data) return
    setCleared(prev => {
      const present = new Set(data.items.map(it => it.pr_number))
      const next = new Set([...prev].filter(n => present.has(n)))
      return next.size === prev.size ? prev : next
    })
  }, [data])

  const items = useMemo(
    () => [...(data?.items ?? [])]
      .filter(it => !cleared.has(it.pr_number))
      .sort((a, b) => b.age_seconds - a.age_seconds),
    [data, cleared],
  )

  // No pending items and nothing to report (or the endpoint is degraded) —
  // stay quiet rather than adding an empty card to every page load.
  if (!data || (data.available && items.length === 0)) return null

  const handleDone = (prNumber: number) => {
    setCleared(prev => new Set(prev).add(prNumber))
    setModal(null)
    onRefresh()
  }

  return (
    <Card
      icon="eye" title="Marketing Approvals" endpoint="/hub/mission/marketing-pending"
      right={!data.available
        ? undefined
        : items.length > 0 ? <Pill variant="warn">{items.length}</Pill> : <Pill variant="ok">clear</Pill>}
      bodyPad={items.length === 0}
    >
      {!data.available && <Unavailable />}
      {data.available && items.length === 0 && (
        <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Nothing awaiting visual approval.
        </div>
      )}
      {items.map((it, i) => (
        <div
          key={it.pr_number}
          className="row"
          style={{
            padding: '8px 12px', gap: 10,
            borderBottom: i === items.length - 1 ? 0 : '1px solid var(--border)',
          }}
        >
          <a
            href={it.github_url}
            target="_blank"
            rel="noreferrer"
            className="row"
            style={{ flex: 1, minWidth: 0, gap: 10, textDecoration: 'none' }}
          >
            <StatusDot tone="warn" />
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)', flex: '0 0 44px' }}>
              #{it.pr_number}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.title}
            </span>
            <Icon name="arrow-up-right" size={11} style={{ color: 'var(--text-dim)' }} />
          </a>
          <AgeBadge seconds={it.age_seconds} />
          <GateActionButton label="✓ Approve" onClick={() => setModal({ mode: 'approve', item: it })} />
          <GateActionButton label="✕ Reject" onClick={() => setModal({ mode: 'reject', item: it })} />
        </div>
      ))}
      {modal && (
        <MarketingActionModal
          mode={modal.mode}
          item={modal.item}
          onCancel={() => setModal(null)}
          onDone={handleDone}
        />
      )}
    </Card>
  )
}

/** Confirm-to-act modal for a marketing PR's visual review — approve clears
 *  needs-human (auto-merger ships it next tick); reject requires a note,
 *  which files a followup issue for Quinn and closes the PR. Mirrors
 *  GateClearModal's structure/disabled-state logic. */
function MarketingActionModal({ mode, item, onCancel, onDone }: {
  mode: 'approve' | 'reject'
  item: HubMissionMarketingPRItem
  onCancel: () => void
  onDone: (prNumber: number) => void
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'approve') {
        await hubMissionMarketing.approve(item.pr_number)
      } else {
        await hubMissionMarketing.reject(item.pr_number, text)
      }
      onDone(item.pr_number)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const confirmDisabled = busy || (mode === 'reject' && text.trim() === '')

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onCancel() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', color: 'var(--text)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)', padding: 18, width: 460, maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {mode === 'approve' ? 'Approve for merge?' : 'Reject and route back to Quinn'}
        </div>
        <div className="row" style={{ gap: 8, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>#{item.pr_number}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>{item.title}</span>
        </div>
        {mode === 'approve' ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Clears <code>needs-human</code> and posts an approval comment. Ships on the next green-CI auto-merge tick.
          </div>
        ) : (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What needs to change?"
              rows={4}
              autoFocus
              disabled={busy}
              style={{
                padding: 8, fontSize: 13, resize: 'vertical',
                borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--bg-elev)', color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Closes this PR and files a followup issue for Quinn with your note.
            </div>
          </>
        )}
        {error && (
          <div style={{ fontSize: 12, color: 'var(--err)' }}>{error}</div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '6px 12px', fontSize: 12.5,
              borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text)',
              fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={confirmDisabled}
            style={{
              padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
              borderRadius: 'var(--radius)',
              border: `1px solid ${mode === 'reject' ? 'var(--err)' : 'var(--ok)'}`,
              background: mode === 'reject' ? 'var(--err)' : 'var(--ok)',
              color: 'var(--accent-fg)',
              fontFamily: 'inherit',
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              opacity: confirmDisabled ? 0.6 : 1,
            }}
          >
            {busy
              ? (mode === 'approve' ? 'Approving…' : 'Rejecting…')
              : (mode === 'approve' ? 'Approve' : 'Reject')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Panel: Release Freshness ────────────────────────────────────────────
// One donut-ringed platform icon per published channel. The ring + icon color
// map to how far behind the published build is (staleness), so Joe can scan
// which surfaces are lagging main at a glance. Mobile: the widgets sit in an
// auto-fit grid that collapses to a single column well before 820px.

/** staleness → hub tone token. unreleased/unknown render dim (grey). */
const RELEASE_TONE: Record<HubReleaseStaleness, 'ok' | 'info' | 'warn' | 'err' | 'dim'> = {
  fresh: 'ok',
  aging: 'info',
  stale: 'warn',
  critical: 'err',
  unreleased: 'dim',
  unknown: 'dim',
}

const RELEASE_TONE_VAR: Record<'ok' | 'info' | 'warn' | 'err' | 'dim', string> = {
  ok: 'var(--ok)',
  info: 'var(--info)',
  warn: 'var(--warn)',
  err: 'var(--err)',
  dim: 'var(--text-dim)',
}

/** Platform → hub Icon name. iOS/Android share 'smartphone' (no dedicated
 *  Apple/Android glyph in the hub set) — the track label disambiguates them. */
function releaseIcon(platform: string): IconName {
  switch (platform) {
    case 'web': return 'globe'
    case 'desktop': return 'monitor'
    case 'ios': return 'smartphone'
    case 'android': return 'smartphone'
    default: return 'package'
  }
}

/** Short store/track label; empty for the self-evident web/desktop production. */
function releaseTrackLabel(ch: HubReleaseChannel): string {
  if (ch.platform === 'ios') return ch.track === 'testflight' ? 'TestFlight' : 'App Store'
  if (ch.platform === 'android') return ch.track === 'production' ? 'Play Store' : 'Testing'
  return ''
}

/** The "N behind" summary line for a channel. */
function releaseBehindText(ch: HubReleaseChannel): string {
  if (ch.status === 'unreleased') return 'Not released yet'
  if (ch.commits_behind === null) return '—'
  if (ch.commits_behind === 0) return 'up to date'
  if (ch.note === '100+') return '100+ behind'
  return `${ch.commits_behind} behind`
}

/** Inline donut: a full track ring plus a colored arc proportional to the
 *  capped staleness fraction (min(behind,20)/20). fresh renders a full ring
 *  (complete = up to date); unreleased renders a dashed hollow ring. */
function ReleaseDonut({ ch }: { ch: HubReleaseChannel }) {
  const size = 62
  const stroke = 5
  const r = (size - stroke) / 2
  const cx = size / 2
  const circ = 2 * Math.PI * r
  const tone = RELEASE_TONE[ch.staleness] ?? 'dim'
  const color = RELEASE_TONE_VAR[tone]

  const unreleased = ch.status === 'unreleased'
  const unknown = ch.commits_behind === null && !unreleased
  // fresh (0 behind) = full ring; otherwise proportional, capped at 20.
  const fraction = ch.staleness === 'fresh'
    ? 1
    : Math.min(ch.commits_behind ?? 0, 20) / 20
  const arc = circ * fraction

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        {/* track ring */}
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke={unreleased ? color : 'var(--border)'}
          strokeWidth={stroke}
          strokeDasharray={unreleased ? '3 4' : undefined}
          opacity={unreleased ? 0.7 : 1}
        />
        {/* colored progress arc — omitted for unreleased / unknown */}
        {!unreleased && !unknown && fraction > 0 && (
          <circle
            cx={cx} cy={cx} r={r} fill="none"
            stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${arc} ${circ - arc}`}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        )}
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={releaseIcon(ch.platform)} size={22} style={{ color }} />
      </div>
    </div>
  )
}

function ReleaseChannelCard({ ch }: { ch: HubReleaseChannel }) {
  const track = releaseTrackLabel(ch)
  const tone = RELEASE_TONE[ch.staleness] ?? 'dim'
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      padding: '12px 8px', background: 'var(--surface-2)', borderRadius: 8,
      textAlign: 'center',
    }}>
      <ReleaseDonut ch={ch} />
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{ch.name}</div>
      {track && (
        <span className="pill ghost" style={{ fontSize: 9.5 }}>{track}</span>
      )}
      <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
        {ch.published_version || '—'}
      </div>
      <div style={{
        fontSize: 11.5, fontWeight: 600,
        color: tone === 'dim' ? 'var(--text-muted)' : RELEASE_TONE_VAR[tone],
      }}>
        {releaseBehindText(ch)}
      </div>
    </div>
  )
}

function ReleaseFreshnessPanel({ data }: { data: HubReleaseFreshnessResponse | null }) {
  const channels = data?.channels ?? []
  return (
    <Card
      icon="package" title="Release Freshness"
      endpoint="/hub/mission/release-freshness"
      bodyPad={false}
    >
      {!data && <div style={{ padding: 12 }}><Unavailable /></div>}
      {data && channels.length === 0 && (
        <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>
          No release channels configured.
        </div>
      )}
      {data && channels.length > 0 && (
        <div style={{
          padding: 12,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 8,
        }}>
          {channels.map(ch => <ReleaseChannelCard key={ch.id} ch={ch} />)}
        </div>
      )}
    </Card>
  )
}

// ── Panel 2: Incidents ──────────────────────────────────────────────────

function IncidentsPanel({ data }: { data: HubMissionIncidentsResponse | null }) {
  const alarms = data?.alarm_issues ?? []
  const errors = data?.errors ?? []
  const count = alarms.length + errors.length
  const hot = count > 0
  return (
    <Card
      icon="alert-triangle" title="Incidents" endpoint="/hub/mission/incidents"
      right={data
        ? hot
          ? <Pill variant="err">{count} active</Pill>
          : <Pill variant="ok">all clear</Pill>
        : undefined}
      style={hot ? { borderColor: 'var(--err)' } : undefined}
      bodyPad={!hot}
    >
      {!data && <Unavailable />}
      {data && !hot && (
        <div className="row" style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)', gap: 8 }}>
          <StatusDot tone="ok" />
          <span>No incidents — prod is quiet.</span>
          {data.prod_deployed_at && (
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              last deploy {relTime(data.prod_deployed_at)} ago
            </span>
          )}
        </div>
      )}
      {data && hot && (
        <>
          {alarms.map((it, i) => (
            <IssueRow
              key={`a-${it.issue_number}`}
              href={it.github_url}
              last={errors.length === 0 && i === alarms.length - 1}
              dotTone="err"
              number={it.issue_number}
              title={it.title}
              trailing={
                <>
                  <PriorityPill priority={it.priority} />
                  <AgeBadge seconds={it.age_seconds} />
                </>
              }
            />
          ))}
          {errors.map((e, i) => (
            <div
              key={`e-${e.error_code}-${e.endpoint}-${i}`}
              className="row"
              style={{
                padding: '8px 12px', gap: 10,
                borderBottom: i < errors.length - 1 ? '1px solid var(--border)' : 0,
              }}
            >
              <StatusDot tone="err" />
              <span className="mono" style={{ fontSize: 12, color: 'var(--err)' }}>{e.error_code}</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.endpoint}
              </span>
              <span style={{ flex: 1, fontSize: 11.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.message}
              </span>
              <Pill variant="err"><span className="mono">{e.count_48h}× 48h</span></Pill>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{relTime(e.last_seen)}</span>
            </div>
          ))}
          {data.prod_deployed_at && (
            <div className="mono" style={{ padding: '6px 12px', fontSize: 10.5, color: 'var(--text-dim)' }}>
              last deploy {relTime(data.prod_deployed_at)} ago
            </div>
          )}
        </>
      )}
    </Card>
  )
}

// ── Panel 3: Objectives ─────────────────────────────────────────────────

function ObjectivesPanel({ data }: { data: HubMissionObjectivesResponse | null }) {
  // Frame objective (OBJECTIVE-0) leads; keep the rest in server order.
  const objectives = useMemo(() => {
    const list = data?.objectives ?? []
    return [...list.filter(o => o.is_frame), ...list.filter(o => !o.is_frame)]
  }, [data])
  const q = data?.queue
  return (
    <Card icon="flame" title="Objectives" endpoint="/hub/mission/objectives" bodyPad={!data || objectives.length === 0}>
      {!data && <Unavailable />}
      {data && objectives.length === 0 && (
        <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          No standing objectives filed yet.
        </div>
      )}
      {data && objectives.map((o, i) => (
        <IssueRow
          key={o.issue_number}
          href={o.github_url}
          last={false}
          dotTone={o.is_frame ? 'info' : 'dim'}
          number={o.issue_number}
          title={o.title}
          trailing={
            <>
              {o.is_frame && <Pill variant="info">frame</Pill>}
              <ObjectiveProgressPills progress={o.progress} />
            </>
          }
        />
      ))}
      {data && (
        <div style={{ padding: '12px 12px 4px', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          Whole queue (company-wide, not objective-scoped)
        </div>
      )}
      {data && (
        <div style={{
          padding: '0 12px 12px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8,
        }}>
          <StatCell label="Queued" value={q?.queued_total ?? 0} tone={undefined} />
          <StatCell label="P0" value={q?.by_priority.p0 ?? 0} tone={(q?.by_priority.p0 ?? 0) > 0 ? 'err' : 'dim'} />
          <StatCell label="P1" value={q?.by_priority.p1 ?? 0} tone="warn" />
          <StatCell label="P2 / P3" value={`${q?.by_priority.p2 ?? 0} / ${q?.by_priority.p3 ?? 0}`} tone="dim" />
          <StatCell label="Oldest queued" value={<span className="mono">{fmtAgeSecs(q?.oldest_queued_secs ?? (q?.oldest_queued_days ?? 0) * 86400)}</span>} tone={undefined} />
          <StatCell label="Shipped 7d" value={data.shipped_7d} tone="ok" />
        </div>
      )}
    </Card>
  )
}

/** Per-objective progress, scoped to that objective's `objective:N` label
 *  (HUB-OBJECTIVE-TAGGING, #1249) — replaces the old whole-queue proxy that
 *  every objective row shared. All-zero until issues carry the label. */
function ObjectiveProgressPills({ progress }: { progress: HubMissionObjective['progress'] }) {
  return (
    <span className="row" style={{ gap: 6 }} title="Progress scoped to this objective's label">
      <Pill variant="ghost">
        <span className="mono">{progress.queued}</span> queued
      </Pill>
      <Pill variant="ok">
        <span className="mono">{progress.shipped_7d}</span> shipped 7d
      </Pill>
      {progress.blocked > 0 && (
        <Pill variant="err">
          <span className="mono">{progress.blocked}</span> blocked
        </Pill>
      )}
    </span>
  )
}

function StatCell({ label, value, tone }: {
  label: string
  value: React.ReactNode
  tone?: 'ok' | 'warn' | 'err' | 'dim'
}) {
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 6, padding: '8px 10px' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {label}
        </span>
        {tone && <StatusDot tone={tone} />}
      </div>
      <div className="mono" style={{ fontSize: 18, marginTop: 2 }}>{value}</div>
    </div>
  )
}

// ── Panel 4: Idea Inbox ─────────────────────────────────────────────────

function InboxPanel({ data }: { data: HubMissionInboxResponse | null }) {
  const items = data?.items ?? []
  return (
    <Card
      icon="inbox" title="Idea Inbox" endpoint="/hub/mission/inbox"
      right={data && items.length > 0 ? <Pill variant="ghost">{data.total}</Pill> : undefined}
      bodyPad={items.length === 0}
    >
      {!data && <Unavailable />}
      {data && items.length === 0 && (
        <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Inbox zero — nothing awaiting triage.
        </div>
      )}
      {items.map((it, i) => (
        <IssueRow
          key={it.issue_number}
          href={it.github_url}
          last={i === items.length - 1}
          dotTone="dim"
          number={it.issue_number}
          title={it.title}
          trailing={
            <>
              <PriorityPill priority={it.priority} />
              {it.origin_planner && <Pill variant="info">planner</Pill>}
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{fmtAgeSecs(it.age_seconds)}</span>
            </>
          }
        />
      ))}
    </Card>
  )
}

// ── Panel 5: Fleet Health ───────────────────────────────────────────────

const FLEET_TONE: Record<HubMissionFleetItem['status'], 'ok' | 'warn' | 'err' | 'dim'> = {
  alive: 'ok',
  quiet: 'warn',
  dark: 'err',
  // "idle" only occurs for event-driven agents, which fleetTone() dims before
  // consulting this map; kept here so the Record stays total over the status union.
  idle: 'dim',
}

// Dot tone for an agent: scheduled agents (SCHEDULED_AGENTS, declared at the
// top of this file) get full liveness alarm colors — a "dark" scheduled agent
// renders red. Event-driven agents (e.g. tenon:hub:operator = manual hub
// actions) render green only when actively alive, else calm dim — silence is
// normal for them, never red.
function fleetTone(a: HubMissionFleetItem): 'ok' | 'warn' | 'err' | 'dim' {
  if (!SCHEDULED_AGENTS.has(a.agent_id)) return a.status === 'alive' ? 'ok' : 'dim'
  return FLEET_TONE[a.status] ?? 'warn'
}

function FleetPanel({ data, onOpenAgent }: {
  data: HubMissionFleetResponse | null
  onOpenAgent: (agentId: string) => void
}) {
  const agents = data?.agents ?? data?.items ?? []
  // Only scheduled agents going dark is an alarm-worthy count.
  const darkCount = agents.filter(a => a.status === 'dark' && SCHEDULED_AGENTS.has(a.agent_id)).length
  return (
    <Card
      icon="activity" title="Fleet Health" endpoint="/hub/mission/fleet"
      right={data
        ? darkCount > 0
          ? <Pill variant="err">{darkCount} dark</Pill>
          : agents.length > 0 ? <Pill variant="ok">{agents.length} agents</Pill> : undefined
        : undefined}
      bodyPad={agents.length === 0}
    >
      {!data && <Unavailable />}
      {data && agents.length === 0 && (
        <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          No signals yet — fleet audit log is empty.
        </div>
      )}
      {agents.map((a, i) => {
        const lastSignal = latestIso(a.last_action_at, a.last_heartbeat_at)
        const displayName = AGENT_DISPLAY_NAMES[a.agent_id]
        const openAgent = () => onOpenAgent(a.agent_id)
        return (
          <div
            key={a.agent_id}
            className="row"
            role="button"
            tabIndex={0}
            aria-label={`Open ${displayName || a.agent_id} detail`}
            onClick={openAgent}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAgent() } }}
            style={{
              padding: '8px 12px', gap: 10, cursor: 'pointer',
              borderBottom: i < agents.length - 1 ? '1px solid var(--border)' : 0,
            }}
          >
            <StatusDot tone={fleetTone(a)} />
            <span className="mono" style={{ fontSize: 12.5, color: 'var(--text)', flex: '0 0 160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName
                ? <>{displayName} <span style={{ color: 'var(--text-dim)' }}>· {a.agent_id.split(':').pop()}</span></>
                : a.agent_id}
            </span>
            <span style={{ flex: 1, fontSize: 11.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {a.last_action || '—'}
            </span>
            {a.cadence && (
              <span
                className="mono"
                title="Expected cadence"
                style={{ fontSize: 10.5, color: 'var(--text-dim)', flex: '0 0 auto', whiteSpace: 'nowrap' }}
              >
                {a.cadence}
              </span>
            )}
            <Pill variant="ghost">
              {/* Event-driven agents with no sessions show "—" rather than a "0"
                  that reads as brokenness; a real nonzero count still renders. */}
              <span className="mono">
                {SCHEDULED_AGENTS.has(a.agent_id) || a.session_count_7d > 0
                  ? `${a.session_count_7d} · 7d`
                  : '—'}
              </span>
            </Pill>
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', flex: '0 0 44px', textAlign: 'right' }}>
              {lastSignal ? relTime(lastSignal) : '—'}
            </span>
            <Icon name="arrow-up-right" size={11} style={{ color: 'var(--text-dim)' }} />
          </div>
        )
      })}
    </Card>
  )
}

// ── Panel 6: COO Overview ───────────────────────────────────────────────

/** Business KPIs (#1250). Reuses StatCell (defined for the Objectives panel's
 *  queue-summary row) — no new design pass, per the ticket's acceptance
 *  criteria. Revenue/cost/feature-adoption render an explicit pending pill
 *  rather than a fabricated number when the data isn't available yet. */
function COOOverviewPanel({ data }: { data: HubMissionCOOOverviewResponse | null }) {
  return (
    <Card icon="trending-up" title="COO Overview" endpoint="/hub/mission/coo-overview" bodyPad={!data}>
      {!data && <Unavailable />}
      {data && (
        <div style={{
          padding: '0 12px 12px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8,
        }}>
          <StatCell label="Tenants" value={data.tenants.total} tone={undefined} />
          <StatCell label="Weekly active users" value={data.weekly_active_users} tone={undefined} />
          <StatCell label="Agent sessions 7d" value={data.agent_activity.sessions_7d} tone={undefined} />
          <StatCell label="Agent actions 7d" value={data.agent_activity.actions_7d} tone="dim" />
          <PendingCell label="Feature adoption" widget={data.feature_adoption} />
          <PendingCell label="Revenue" widget={data.revenue} />
          <PendingCell label="Agent cost" widget={data.cost} />
        </div>
      )}
      {data && data.feature_adoption.available && data.feature_adoption.items && (
        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.feature_adoption.items.map(it => (
            <div key={it.event} className="row mono" style={{ fontSize: 11, color: 'var(--text-muted)', gap: 8 }}>
              <span>{it.event}</span>
              <span className="grow" style={{ flex: 1 }} />
              <span>{it.distinct_users_7d} users · 7d</span>
            </div>
          ))}
          {data.feature_adoption.note && (
            <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{data.feature_adoption.note}</div>
          )}
        </div>
      )}
    </Card>
  )
}

/** A StatCell variant for widgets that may not have data yet (available:
 *  false) — shows a muted "pending" pill + note instead of a number, so an
 *  unmeasured metric is never confused with a real zero. */
function PendingCell({ label, widget }: {
  label: string
  widget: { available: boolean; note?: string }
}) {
  if (widget.available) {
    return <StatCell label={label} value="live" tone="ok" />
  }
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 6, padding: '8px 10px' }} title={widget.note}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {label}
        </span>
        <StatusDot tone="dim" />
      </div>
      <div className="mono" style={{ fontSize: 12, marginTop: 2, color: 'var(--text-dim)' }}>pending</div>
    </div>
  )
}

// ── Panel 7: CS Support Metrics ─────────────────────────────────────────

const CS_CATEGORY_COLORS = [
  'var(--accent)',
  'var(--info)',
  'var(--ok)',
  'var(--warn)',
  'var(--err)',
  'var(--text-faint)',
]

/** Support metrics (#2111): deflection rate, response latency, category
 *  breakdown, volume trend. Reuses Donut/Bars (HubOverviewPage's Plan mix +
 *  Business page pattern) and StatCell/PendingCell (COO panel) — no new
 *  design language, per the ticket's Design contract note. Deflection rate
 *  and response latency are proxies computed from whether a ticket's filed
 *  GitHub issue closed (see docs/cs/support-strategy.md §7) — response
 *  latency shows a "pending" cell rather than a fabricated number when no
 *  ticket has resolved yet. */
function CSMetricsPanel({ data }: { data: HubMissionCSMetricsResponse | null }) {
  const categories = data?.categories ?? []
  const trend = data?.volume_trend ?? []
  const catSegments = categories.map((c, i) => ({
    value: Math.max(c.count, 0.001),
    color: CS_CATEGORY_COLORS[i % CS_CATEGORY_COLORS.length],
  }))
  // Deflection-rate-over-time (AC4): per-day rate from the same trend points
  // the volume Bars chart already uses, so "is deflection trending up" is
  // answerable without a second endpoint. Days with zero tickets read as 0%
  // rather than being dropped, matching the zero-filled volume series.
  const deflectionTrend = trend.map(p => (p.count > 0 ? (p.deflected / p.count) * 100 : 0))

  return (
    <Card icon="inbox" title="CS Support Metrics" endpoint="/hub/mission/cs-metrics" bodyPad={!data}>
      {!data && <Unavailable />}
      {data && (
        <>
          <div style={{
            padding: '0 12px 12px',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8,
          }}>
            <StatCell label={`Tickets (${data.window_days}d)`} value={data.total} tone={undefined} />
            <StatCell
              label="Deflection rate"
              value={`${Math.round(data.deflection.rate * 100)}%`}
              tone={data.deflection.total === 0 ? 'dim' : undefined}
            />
            {data.response_latency.available ? (
              <StatCell
                label="Median response"
                value={`${data.response_latency.median_hours!.toFixed(1)}h`}
                tone={undefined}
              />
            ) : (
              <PendingCell label="Median response" widget={data.response_latency} />
            )}
          </div>
          <div style={{ padding: '0 12px 12px', fontSize: 10.5, color: 'var(--text-dim)' }} title={data.deflection.note}>
            {data.deflection.note}
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
              Deflection trend ({data.window_days}d)
            </div>
            <Sparkline data={deflectionTrend} height={28} />
          </div>
          <div style={{ padding: '0 12px 12px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: '1 1 200px' }}>
              <Donut size={72} segments={catSegments.length > 0 ? catSegments : [{ value: 1, color: 'var(--text-faint)' }]} />
              <div style={{ flex: 1, fontSize: 11.5, minWidth: 0 }}>
                {categories.length === 0 && (
                  <span style={{ color: 'var(--text-dim)' }}>No tickets in window</span>
                )}
                {categories.map((c, i) => (
                  <div key={c.category} className="row" style={{ justifyContent: 'space-between', padding: '2px 0' }}>
                    <span className="row" style={{ gap: 6 }}>
                      <span className="dot" style={{ background: CS_CATEGORY_COLORS[i % CS_CATEGORY_COLORS.length] }} />
                      <span>{c.category}</span>
                    </span>
                    <span className="mono">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 160 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
                Volume trend ({data.window_days}d)
              </div>
              <Bars data={trend.map(p => p.count)} height={60} />
            </div>
          </div>
        </>
      )}
    </Card>
  )
}

/** Display names for known scheduled agents (mirror of the server identity map). */
const AGENT_DISPLAY_NAMES: Record<string, string> = {
  'tenon:eng:coordinator': 'Riley',
  'tenon:eng:ship-reviewer': 'Cass',
  'tenon:eng:spawn-coder': 'Alex',
  'tenon:product:objective-planner': 'Morgan',
  'tenon:marketing:coordinator': 'Quinn',
  'tenon:cs:coordinator': 'Sam',
}

// ── Agent detail drawer (#1258) ─────────────────────────────────────────

/** Right-side slide-over with backdrop. Closes on Escape or backdrop click.
 *  Reuses the hub drawer discipline (overlay 0.4, right panel, token colors)
 *  from the Engineering QueueDrawer (commit 4a099f47). */
function AgentDrawer({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const [data, setData] = useState<HubMissionAgentResponse | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(false)
    hubMission.agent(agentId)
      .then(d => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setError(true) })
    return () => { cancelled = true }
  }, [agentId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const identity = data?.identity
  const displayName = identity?.display_name ?? AGENT_DISPLAY_NAMES[agentId] ?? agentId

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${displayName} detail`}
        style={{
          width: 520, maxWidth: '92vw', height: '100%',
          background: 'var(--surface)', borderLeft: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div className="row" style={{ padding: '12px 16px', gap: 10, borderBottom: '1px solid var(--border)' }}>
          <Icon name="activity" size={14} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{displayName}</span>
          <span className="grow" style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 16, lineHeight: 1 }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && !data && (
            <div style={{ fontSize: 12, color: 'var(--err)' }}>Failed to load agent detail.</div>
          )}
          {!error && !data && (
            <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div>
          )}

          {identity && (
            <>
              {/* Identity card */}
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{identity.display_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{identity.role}</div>
                <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <Pill variant="info">{identity.cadence}</Pill>
                  <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{identity.agent_id}</span>
                </div>
              </div>

              {/* Latest tick summary */}
              <Section title="Latest tick summary">
                {data?.latest_tick_summary ? (
                  <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>
                      {data.latest_tick_summary.summary || <span style={{ color: 'var(--text-dim)' }}>(empty summary)</span>}
                    </div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 6 }}>
                      {relTime(data.latest_tick_summary.created_at)} ago
                    </div>
                  </div>
                ) : (
                  <EmptyLine>No tick summaries yet.</EmptyLine>
                )}
              </Section>

              {/* Recent activity */}
              <Section title="Recent activity" count={data?.recent_actions.length}>
                {data && data.recent_actions.length === 0 && <EmptyLine>No recent activity.</EmptyLine>}
                {data?.recent_actions.map((a, i) => (
                  <div
                    key={i}
                    style={{ padding: '7px 0', borderBottom: i < data.recent_actions.length - 1 ? '1px solid var(--border)' : 0 }}
                  >
                    <div className="row" style={{ gap: 8 }}>
                      <span className="mono" style={{ fontSize: 11.5, color: 'var(--text)' }}>{a.action}</span>
                      {a.target_table && (
                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
                          {a.target_table}{a.target_id ? `/${a.target_id}` : ''}
                        </span>
                      )}
                      <span className="grow" style={{ flex: 1 }} />
                      <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{relTime(a.created_at)}</span>
                    </div>
                    <IssuesTouched payload={a.payload} />
                  </div>
                ))}
              </Section>

              {/* Sessions */}
              <Section title="Sessions" count={data?.sessions.length}>
                {data && data.sessions.length === 0 && <EmptyLine>No sessions recorded.</EmptyLine>}
                {data?.sessions.map((s, i) => (
                  <div key={i} className="row" style={{ gap: 8, padding: '6px 0', borderBottom: i < data.sessions.length - 1 ? '1px solid var(--border)' : 0 }}>
                    <StatusDot tone={SESSION_TONE[s.status] ?? 'dim'} />
                    <span style={{ fontSize: 11.5, color: 'var(--text)' }}>{s.status}</span>
                    <span className="grow" style={{ flex: 1 }} />
                    <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
                      started {relTime(s.started_at)} ago{s.ended_at ? ` · ${sessionDuration(s.started_at, s.ended_at)}` : ' · active'}
                    </span>
                  </div>
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const SESSION_TONE: Record<string, 'ok' | 'warn' | 'err' | 'info' | 'dim'> = {
  running: 'info',
  completed: 'ok',
  paused: 'warn',
  errored: 'err',
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {title}
        </span>
        {typeof count === 'number' && count > 0 && (
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  )
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '4px 0' }}>{children}</div>
}

/** Render payload.issues_touched (array of issue numbers) as GitHub links. */
function IssuesTouched({ payload }: { payload: any }) {
  const raw = payload && typeof payload === 'object' ? payload.issues_touched : undefined
  if (!Array.isArray(raw) || raw.length === 0) return null
  const nums = raw
    .map((n: any) => (typeof n === 'number' ? n : parseInt(String(n), 10)))
    .filter((n: number) => Number.isFinite(n))
  if (nums.length === 0) return null
  return (
    <div className="row" style={{ gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
      {nums.map((n: number) => (
        <a
          key={n}
          href={`${GITHUB_REPO_ISSUE_BASE}/${n}`}
          target="_blank"
          rel="noreferrer"
          className="pill ghost"
          style={{ fontSize: 10, textDecoration: 'none' }}
        >
          #{n}
        </a>
      ))}
    </div>
  )
}

/** Human duration between two ISO timestamps. */
function sessionDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (!isFinite(ms) || ms < 0) return '—'
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  const rem = min % 60
  return rem ? `${hr}h ${rem}m` : `${hr}h`
}

// ── shared bits ─────────────────────────────────────────────────────────

/** One issue row — the whole row deep-links to the GitHub issue. */
function IssueRow({
  href, last, dotTone, number, title, trailing,
}: {
  href: string
  last: boolean
  dotTone: 'ok' | 'warn' | 'err' | 'info' | 'dim'
  number: number
  title: string
  trailing?: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="row"
      style={{
        padding: '8px 12px', gap: 10, textDecoration: 'none',
        borderBottom: last ? 0 : '1px solid var(--border)',
      }}
    >
      <StatusDot tone={dotTone} />
      <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)', flex: '0 0 44px' }}>
        #{number}
      </span>
      <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </span>
      {trailing}
      <Icon name="arrow-up-right" size={11} style={{ color: 'var(--text-dim)' }} />
    </a>
  )
}

function Unavailable() {
  return (
    <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-dim)' }}>
      unavailable
    </div>
  )
}

/** The later of two ISO timestamps (either may be empty). */
function latestIso(a?: string, b?: string): string | null {
  const ta = a ? new Date(a).getTime() : NaN
  const tb = b ? new Date(b).getTime() : NaN
  if (isNaN(ta) && isNaN(tb)) return null
  if (isNaN(tb) || ta >= tb) return a!
  return b!
}

